import { FC } from 'react'
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Check } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'
import { useIntl } from 'react-intl'

import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import PasswordField from 'components/PasswordField'
import { useUserPassword } from 'services/profile/profile.hooks'
import theme from 'styles/theme'

import { cancelButtonSx, profileFieldSx, saveButtonSx } from './userForm.styles'

interface UserPasswordProps {
  onBack: () => void
}

const UserPassword: FC<UserPasswordProps> = ({ onBack }) => {
  const { formatMessage } = useIntl()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const { enqueueSnackbar } = useSnackbar()
  const { useUserPasswordUpdate } = useUserPassword()
  const labelBg = isDesktop ? theme.r4r.greyBg : '#fff'

  const title = formatMessage({ id: 'userPassword.changePassword', defaultMessage: 'Zmień hasło' })
  const subtitle = formatMessage({
    id: 'userPassword.subtitle',
    defaultMessage: 'Ustaw nowe hasło do swojego konta.'
  })

  const form = useFormik<{
    currentPassword: string
    newPassword: string
    retypeNewPassword: string
  }>({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      retypeNewPassword: ''
    },
    onSubmit: async (values, { setErrors }) => {
      try {
        await useUserPasswordUpdate.mutateAsync(values)
        enqueueSnackbar(
          formatMessage({ id: 'userPassword.success', defaultMessage: 'Hasło zostało zmienione.' }),
          { variant: 'success' }
        )
        onBack()
      } catch (e: any) {
        if (e?.response?.data) {
          setErrors(e.response.data)
        }
        enqueueSnackbar(
          formatMessage({ id: 'userPassword.error', defaultMessage: 'Nie można zmienić hasła.' }),
          { variant: 'error' }
        )
      }
    }
  })

  const content = (
    <Box component='form' onSubmit={form.handleSubmit} sx={{ width: '100%', maxWidth: '560px' }}>
      {isDesktop && (
        <>
          <Typography sx={{ fontSize: '18px', fontWeight: 800, color: theme.r4r.navy }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: theme.r4r.grey, mt: '8px', mb: '28px' }}>
            {subtitle}
          </Typography>
        </>
      )}
      {!isDesktop && (
        <Typography sx={{ fontSize: '13px', color: theme.r4r.grey, mb: '20px' }}>
          {subtitle}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <PasswordField
          name='currentPassword'
          fullWidth
          label={formatMessage({
            id: 'userPassword.currentPassword',
            defaultMessage: 'Obecne hasło'
          })}
          InputLabelProps={{ shrink: true }}
          sx={profileFieldSx(false, labelBg)}
          disabled={form.isSubmitting}
          onChange={form.handleChange}
          value={form.values.currentPassword}
          error={!!form.errors.currentPassword && !!form.touched.currentPassword}
          helperText={form.errors.currentPassword}
        />
        <PasswordField
          name='newPassword'
          fullWidth
          label={formatMessage({
            id: 'userPassword.newPassword',
            defaultMessage: 'Hasło'
          })}
          InputLabelProps={{ shrink: true }}
          sx={profileFieldSx(false, labelBg)}
          disabled={form.isSubmitting}
          onChange={form.handleChange}
          value={form.values.newPassword}
          error={!!form.errors.newPassword && !!form.touched.newPassword}
          helperText={form.errors.newPassword}
        />
        <PasswordField
          name='retypeNewPassword'
          fullWidth
          label={formatMessage({
            id: 'userPassword.retypeNewPassword',
            defaultMessage: 'Potwierdz Hasło'
          })}
          InputLabelProps={{ shrink: true }}
          sx={profileFieldSx(false, labelBg)}
          disabled={form.isSubmitting}
          onChange={form.handleChange}
          value={form.values.retypeNewPassword}
          error={!!form.errors.retypeNewPassword && !!form.touched.retypeNewPassword}
          helperText={
            form.errors.retypeNewPassword ||
            formatMessage({
              id: 'userPassword.retypeNewPasswordHelperText',
              defaultMessage: 'Minimum 8 znaków, minimum jeden znak specjalny (@,#,$)'
            })
          }
        />
      </Box>

      <Box sx={{ display: 'flex', gap: '12px', mt: '20px', flexWrap: 'wrap' }}>
        <LoadingButton
          type='submit'
          loading={form.isSubmitting}
          disabled={!form.dirty || form.isSubmitting}
          startIcon={<Check sx={{ fontSize: '16px' }} />}
          sx={saveButtonSx}>
          {formatMessage({ id: 'userPersonalDetails.save', defaultMessage: 'Zapisz zmiany' })}
        </LoadingButton>
        <Button type='button' disabled={form.isSubmitting} onClick={onBack} sx={cancelButtonSx}>
          {formatMessage({ id: 'userPersonalDetails.cancel', defaultMessage: 'Anuluj' })}
        </Button>
      </Box>
    </Box>
  )

  if (isDesktop) {
    return content
  }

  return (
    <>
      <BackNavigationHeader label={title} onBack={onBack} />
      <BackNavigationContent>{content}</BackNavigationContent>
    </>
  )
}

export default UserPassword
